<template>
  <div
    id="conversation_intelligence_templates"
    class="flex flex-col gap-6 proxima_nova"
  >
    <div
      id="templates_header"
      class="flex flex-row justify-between align-center"
    >
      <h1 class="text-xl primary_color font-semibold">
        Conversation Intelligence Templates
      </h1>
      <a-button class="primary_medium" @click="handleCreateTemplate">
        Create Template
      </a-button>
    </div>

    <div
      v-if="loading && templates.length === 0"
      style="height: 60vh"
      class="overflow-hidden"
    >
      <SkeletonLoader :page="loaderPages.NAV_LIST" />
    </div>

    <EmptyScreen
      v-else-if="!loading && templates.length === 0"
      source="conversation_intelligence_templates"
      text="No templates created yet!"
      description="Create a new template to get started"
      padding="my-[30vh]"
    >
      <a-button class="primary_medium mt-4" @click="handleCreateTemplate">
        Create Template
      </a-button>
    </EmptyScreen>

    <div v-else id="templates_content" class="flex flex-col gap-4">
      <SimpleTable
        :headers="templateHeaders"
        :data="templates"
        :show-actions-column="true"
        :actions="actions"
        :custom-column-render="customColumnRender"
        :custom-row="handleRowClick"
        :height="'calc(100vh - (240px + var(--banner-height)))'"
        @scroll="handleScroll"
      />
      <SkeletonLoader
        v-if="isLoadingMore"
        id="templates_loading_more"
        :rows="2"
        :show-avatar="false"
      />
    </div>

    <ConfirmationModal
      v-model:visible="deleteModalVisible"
      title="Delete Template"
      :message="`Are you sure you want to delete '${selectedTemplate?.name}'?`"
      :loading="isDeleting"
      type="delete"
      confirm-text="Delete"
      loading-text="Deleting..."
      @confirm="handleDeleteConfirm"
      @cancel="deleteModalVisible = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { message } from "ant-design-vue";
import CallService from "@/src/api/CallService.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { ciTemplateActions } from "@/src/constants/callConstants.js";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import TemplateTag from "@/src/views/calls/conversation_intelligence/components/TemplateTag.vue";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import { fetchCITemplates } from "@/src/utils/callsUtils.js";

const router = useRouter();

const templates = ref([]);
const loading = ref(false);
const isLoadingMore = ref(false);
const pagination = ref({
  current: 1,
  pageSize: 20,
  total: 0,
});
const deleteModalVisible = ref(false);
const selectedTemplate = ref(null);
const isDeleting = ref(false);

const templateHeaders = [{ label: "Template Name", key: "name", width: 2 }];

const customColumnRender = {
  name: {
    component: TemplateTag,
    props: (record) => ({
      name: record.name,
      showTag: record.is_primary,
      tagText: "Primary",
    }),
  },
};

const hasMoreTemplates = computed(
  () => templates.value.length < pagination.value.total
);

const actions = ciTemplateActions.map((action) => ({
  ...action,
  handler:
    action.label === "Edit"
      ? (row) => handleRowClick(row).onClick()
      : action.label === "Set as Primary"
      ? (row) => handleSetPrimary(row.id)
      : (row) => handleDeleteTemplate(row),
}));

const mapTemplateFromApi = (template) => ({
  id: template.id,
  name: template.template_name,
  is_primary: template.is_primary,
});

const fetchTemplates = async (isInitialLoad = false) => {
  try {
    if (isInitialLoad) {
      loading.value = true;
      pagination.value.current = 1;
    } else {
      isLoadingMore.value = true;
    }

    const currentPage = isInitialLoad
      ? pagination.value.current
      : pagination.value.current + 1;

    const response = await fetchCITemplates(
      currentPage,
      pagination.value.pageSize
    );

    if (response.data && Array.isArray(response.data)) {
      const mappedTemplates = response.data.map(mapTemplateFromApi);

      if (isInitialLoad) {
        templates.value = mappedTemplates;
        pagination.value.total = response.total || 0;
      } else {
        templates.value.push(...mappedTemplates);
        pagination.value.current = currentPage;
      }
    }
  } catch (error) {
    const errorMessage = isInitialLoad
      ? "Failed to fetch templates"
      : "Failed to load more templates";
    console.error(
      `Error ${isInitialLoad ? "fetching" : "loading more"} templates:`,
      error
    );
    message.error(errorMessage);
  } finally {
    if (isInitialLoad) {
      loading.value = false;
    } else {
      isLoadingMore.value = false;
    }
  }
};

const handleCreateTemplate = () => {
  try {
    router.push({ name: "conversation-intelligence-template-create" });
  } catch (error) {
    console.error("Error navigating to create template:", error);
  }
};

const handleDeleteTemplate = (template) => {
  try {
    if (!template?.id) {
      message.error("Invalid template");
      return;
    }
    selectedTemplate.value = template;
    deleteModalVisible.value = true;
  } catch (error) {
    console.error("Error opening delete modal:", error);
  }
};

const handleDeleteConfirm = async () => {
  try {
    if (!selectedTemplate.value?.id) {
      message.error("Invalid template");
      return;
    }

    isDeleting.value = true;
    const response = await CallService.deleteCITemplate(
      selectedTemplate.value.id
    );

    if (response?.success) {
      message.success("Template deleted successfully");
      deleteModalVisible.value = false;
      await fetchTemplates(true);
    }
  } catch (error) {
    console.error("Error deleting template:", error);
    message.error("An error occurred while deleting template");
  } finally {
    isDeleting.value = false;
  }
};

const handleSetPrimary = async (id) => {
  if (!id) {
    console.error("Template ID not found");
    return;
  }
  try {
    const template = templates.value.find((t) => t.id === id);
    const templateName = template?.name || "Template";

    const response = await CallService.setPrimaryCITemplate(id);
    if (response?.success) {
      message.success(`${templateName} set as primary successfully`);
      await fetchTemplates(true);
    }
  } catch (error) {
    console.error("Error setting primary template:", error);
    message.error("Failed to set the template as primary");
  }
};

const handleRowClick = (record) => {
  return {
    onClick: () => {
      try {
        if (record?.id) {
          router.push({
            name: "conversation-intelligence-template-edit",
            params: { id: record.id },
          });
        }
      } catch (error) {
        console.error("Error handling row click:", error);
      }
    },
  };
};

const loadMoreTemplates = async () => {
  if (isLoadingMore.value || !hasMoreTemplates.value) {
    return;
  }

  try {
    await fetchTemplates(false);
  } catch (error) {
    console.error("Error in loadMoreTemplates:", error);
  }
};

const handleScroll = (event) => {
  if (isLoadingMore.value || !hasMoreTemplates.value) {
    return;
  }

  try {
    const target = event.target;
    if (!target) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = target;
    const scrollThreshold = 200;

    if (scrollTop + clientHeight >= scrollHeight - scrollThreshold) {
      loadMoreTemplates();
    }
  } catch (error) {
    console.error("Error handling scroll:", error);
  }
};

onMounted(() => {
  fetchTemplates(true);
});
</script>